import { z } from 'zod';
import { DRAFT_SOURCES, type DraftProposal } from '../../ontology/objects/draft.ts';
import { ACT_STATUSES } from '../../ontology/objects/reading.ts';
import { CLOSED_RECOMMENDATION_STATUSES } from '../../ontology/objects/recommendation.ts';
import { idSchema } from '../../ontology/primitives.ts';
import { addFieldsSchema } from './add.ts';
import { instant } from './primitives.ts';

const wirePostFileSchema = z.object({
  place: z.number().int(),
  kind: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  kept: z.boolean(),
  note: z.string().nullable(),
});

export type WirePostFile = z.infer<typeof wirePostFileSchema>;

const wireReadingSchema = z.object({
  acts: z.array(
    z.object({
      kind: z.enum(['open', 'close', 'move', 'split', 'unclear']),
      said: z.string(),
      status: z.enum(ACT_STATUSES).nullable(),
      line: z.string().nullable(),
      why: z.array(z.string()),
    }),
  ),
  repeatOf: z.string().nullable(),
});

const wireChannelPostSchema = z.object({
  id: idSchema,
  link: z.string(),
  sentAt: instant,
  editedAt: instant.nullable(),
  /** Only sent to the desk and the analyst; followers never receive withdrawn posts. */
  deletedAt: instant.nullable(),
  text: z.string(),
  /** Null is unread (no text, or no model configured), unlike `[]`. */
  symbols: z.array(z.string()).nullable(),
  /** Null for viewers other than the desk and the analyst, and for unread posts. */
  reading: wireReadingSchema.nullable(),
  media: z.array(wirePostFileSchema),
});

export type WireReading = z.infer<typeof wireReadingSchema>;

export type WireChannelPost = z.infer<typeof wireChannelPostSchema>;

export const channelLogSchema = z.object({
  posts: z.array(wireChannelPostSchema),
  next: z.object({ sentAt: instant, id: idSchema }).nullable(),
  symbols: z.array(z.object({ symbol: z.string(), count: z.number().int() })),
});

export type ChannelLogReply = z.infer<typeof channelLogSchema>;

const draftProposalSchema: z.ZodType<DraftProposal> = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('add'),
    fields: addFieldsSchema,
    books: z.array(z.string()),
    newBook: z.string().nullable(),
    asksBook: z.boolean(),
  }),
  z.object({
    kind: z.literal('revise'),
    recommendationId: idSchema,
    targetPrice: z.number().nullable(),
    stoplossPrice: z.number().nullable(),
  }),
  z.object({
    kind: z.literal('close'),
    recommendationId: idSchema,
    price: z.number().nullable(),
  }),
]);

export const wireDraftSchema = z.object({
  id: idSchema,
  title: z.string(),
  body: z.string(),
  proposal: draftProposalSchema,
  source: z.enum(DRAFT_SOURCES),
  sourceLink: z.string().nullable(),
  sourceAt: instant,
  createdAt: instant,
});

export type WireDraft = z.infer<typeof wireDraftSchema>;

export const wireActionSchema = z.object({
  id: idSchema,
  at: instant,
  actor: z.string(),
  actionType: z.string(),
  objectType: z.string(),
  objectId: idSchema,
  input: z.unknown(),
  edits: z.array(z.object({ property: z.string(), from: z.unknown(), to: z.unknown() })),
});

export type WireAction = z.infer<typeof wireActionSchema>;

const momentSchema = z.object({
  id: idSchema,
  at: instant,
  what: z.string(),
  gist: z.string().nullable(),
  tone: z.enum(['up', 'down', 'warn']).nullable(),
  /** A step of the call, the reader's own trade, a warning, or how the call ended. */
  mark: z.enum(['step', 'yours', 'warn', ...CLOSED_RECOMMENDATION_STATUSES]),
  facts: z.array(z.object({ label: z.string(), value: z.string() })),
  actor: z.string(),
});

export type Moment = z.infer<typeof momentSchema>;

export type MomentMark = Moment['mark'];

export const storySchema = z.object({
  moments: z.array(momentSchema),
  recommendedAt: instant.nullable(),
  /** The reader's own positions on it, once any of them is exited; `risked` is the exited legs' 1R. */
  yours: z.object({ pnl: z.number(), risked: z.number().nullable(), open: z.boolean() }).nullable(),
});

export type Story = z.infer<typeof storySchema>;
